/**
 * A player's picture where the VK build showed the player's VK photo (port addition): the main map's player's plate
 * (avatar_container), the rows of the tops (clip_topN.avatar) and the auction's holder (auc_avatar_container). The VK
 * photos are gone with VK; the plate showed an empty white frame and the tops a checkerboard.
 * The game's own art instead: the portrait of the player's specialisation (the choose_spec screen's four worms: shape
 * 994, the melee master, the demolition man, the artilleryman, the engineer; vector art, sharp at the HUD's size), and
 * for a player without one yet the game's picture of a team of worms (the skills' "worms" icon, skill_worms); where
 * there is nobody (the auction without a holder) the game's question icon.
 */
import { Bitmap, Rectangle, Sprite } from '@flash';

/** The portraits of the specialisations in shape 994 (its style groups' outlines, the screen's coordinates). */
const PORTRAITS: Record<number, { x: number; y: number; width: number; height: number }> = {
  1: { x: 137.7, y: 169.1, width: 63.8, height: 70 },
  2: { x: 425.4, y: 169.1, width: 58.75, height: 69.85 },
  3: { x: 137.7, y: 319.55, width: 77.25, height: 70.5 },
  4: { x: 424.75, y: 319.05, width: 59.95, height: 71.1 },
};
const PORTRAIT_SHAPE = 994;
/** The "specialisation" of no player (the auction without a holder: a question mark in its frame). */
export const NOBODY = -1;
/** The picture frames of the original: the main map's plate (50 px, the VK photo's size) and a row of the tops (32). */
export const AVATAR_PLATE = 50;
export const AVATAR_ROW = 32;
/** Room round a portrait in its square (px of the square). */
const PAD = 0.06;
const NAME = '$avatar';

/** A symbol table of the interf bundle (its shapes by character id), from a small class of it. */
let symbols: any = null;
function interfSymbols(fwp: any): any {
  if (symbols && symbols.$ok) return symbols.sym;
  const c = fwp && fwp.container_interf;
  const o = c ? c.get_object('clip_note') : null;
  const sym = o && o.$sym && typeof o.$sym.instantiate === 'function' ? o.$sym : null;
  if (sym) symbols = { sym, $ok: true };
  return sym;
}

/**
 * A `size` x `size` picture (0, 0 at its top left) of a player whose specialisation is `spec` (0: none yet), or null
 * when the art is missing. Not a target of the mouse.
 */
export function avatarArt(fwp: any, spec: number, size: number): Sprite | null {
  const box = new Sprite();
  box.name = NAME;
  box.mouseEnabled = false;
  box.mouseChildren = false;
  const r = PORTRAITS[Math.floor(Number(spec) || 0)];
  if (r) {
    const sym = interfSymbols(fwp);
    const shape = sym ? sym.instantiate(PORTRAIT_SHAPE) : null;
    if (!shape) return null;
    const crop = new Sprite();
    crop.addChild(shape);
    crop.scrollRect = new Rectangle(r.x, r.y, r.width, r.height);
    const k = (size * (1 - 2 * PAD)) / Math.max(r.width, r.height);
    crop.scaleX = crop.scaleY = k;
    crop.x = (size - r.width * k) / 2;
    crop.y = (size - r.height * k) / 2;
    box.addChild(crop);
    return box;
  }
  // (NOBODY: the game's question icon, in the family of the skills' icons: the auction without a holder)
  const bd = fwp && fwp.container_interf ? fwp.container_interf.get_object(spec === NOBODY ? 'icon_question' : 'skill_worms') : null;
  if (!bd || !(bd.width > 0)) return null;
  const b = new Bitmap(bd);
  b.smoothing = true;
  b.width = size;
  b.height = size;
  box.addChild(b);
  return box;
}

/**
 * Puts the picture of `spec` into `container` (a slot of the original's size `size`), replacing the one it showed
 * before (a render may run again); a container that shows another picture already (a VK photo of old) is left alone.
 */
export function showAvatar(fwp: any, container: any, spec: number, size: number): void {
  if (!container || typeof container.addChild !== 'function') return;
  const key = `${Math.floor(Number(spec) || 0)}:${size}`; // (NOBODY: -1)
  const old = container.getChildByName(NAME);
  if (old && old.$avatarKey === key) return;
  if (old) container.removeChild(old);
  for (let i = 0; i < container.numChildren; i++) if (container.getChildAt(i).name !== NAME) return;
  const art = avatarArt(fwp, spec, size);
  if (!art) return;
  (art as any).$avatarKey = key;
  container.addChild(art);
}
